import { useMemo } from 'react';
import HoleSchematic from '../components/HoleSchematic';
import { caddyReadouts, type Club } from '../lib/caddy';
import { distanceM, formatDistance } from '../lib/geo';
import type { Course, LngLat, Weather } from '../lib/types';

/**
 * The hole surface: one hole running straight up the screen, with the caddy
 * talking down both sides of it.
 *
 * A vertical hole leaves most of a landscape screen empty, so the rails are
 * generous — and their contents are not fixed. Readouts are scored for the
 * situation (see lib/caddy.ts) and the best ones fill the slots, so the box
 * that carried the wind on the tee carries the club on the approach. Distances
 * to the green keep the left rail because they are the one thing a golfer looks
 * for in the same place every time.
 *
 * 🟥 CarPlay does NOT mount this. Apple's base view may hold the map and
 * nothing else, so the car screen mounts <HoleSchematic> alone and takes its
 * numbers from CarPlay templates. Every rail added here is free of entitlement
 * risk by construction.
 */

export interface HoleScreenProps {
  course: Course;
  holeNumber: number;
  teeColour: string;
  position?: LngLat | null;
  aim?: LngLat | null;
  weather?: Weather | null;
  unit: 'm' | 'yd';
  bag?: Club[];
  bagConfirmed?: boolean;
  onHoleChange?: (n: number) => void;
}

export default function HoleScreen({
  course, holeNumber, teeColour, position, aim, weather, unit,
  bag, bagConfirmed, onHoleChange,
}: HoleScreenProps) {
  const hole = course.holes.find((h) => h.number === holeNumber) ?? course.holes[0];

  const nums = useMemo(() => {
    if (!hole) return null;
    // From the golfer when we have a fix, from the tee when we do not — quoting
    // tee distances to somebody standing 200 m up the fairway is the single
    // most useless thing a golf app can do.
    const from = position ?? hole.tees[teeColour] ?? Object.values(hole.tees)[0];
    if (!from) return null;
    const d = (p: LngLat) => Math.round(formatDistance(distanceM(from, p), unit));
    const teePos = hole.tees[teeColour] ?? Object.values(hole.tees)[0];
    return {
      back: d(hole.green.back),
      middle: d(hole.green.middle),
      front: d(hole.green.front),
      length: teePos ? Math.round(formatDistance(distanceM(teePos, hole.green.middle), unit)) : null,
    };
  }, [hole, position, teeColour, unit]);

  const advice = useMemo(
    () => (hole ? caddyReadouts({ hole, teeColour, position: position ?? null, weather: weather ?? null, unit, bag, bagConfirmed }) : []),
    [hole, teeColour, position, weather, unit, bag, bagConfirmed],
  );

  if (!hole || !nums) {
    return <div className="hole-surface"><div className="hs-empty">Hole not mapped</div></div>;
  }

  const go = (delta: number) => {
    if (!onHoleChange) return;
    const idx = course.holes.findIndex((h) => h.number === hole.number);
    const next = course.holes[(idx + delta + course.holes.length) % course.holes.length];
    onHoleChange(next.number);
  };

  return (
    <div className="hole-surface">
      <div className="hs-top">
        <span className="hs-holeno">{hole.number}</span>
        <span className="hs-par">
          PAR {hole.par}{nums.length != null && ` · ${nums.length}${unit}`}
          {hole.strokeIndex ? ` · SI ${hole.strokeIndex}` : ''}
        </span>
        {onHoleChange && (
          <div className="hs-flick">
            <button onClick={() => go(-1)} aria-label="Previous hole">‹</button>
            <button onClick={() => go(1)} aria-label="Next hole">›</button>
          </div>
        )}
      </div>

      <div className="hs-stage">
        {/* Distances live in the same place every time — this is the rail a
            golfer's eye goes to without looking. */}
        <div className="hs-rail">
          <div className="hs-box"><span>Back</span><b>{nums.back}</b></div>
          <div className="hs-box mid"><span>Middle</span><b>{nums.middle}</b></div>
          <div className="hs-box"><span>Front</span><b>{nums.front}</b></div>
        </div>

        <div className="hs-figure">
          <HoleSchematic hole={hole} teeColour={teeColour} position={position} aim={aim} />
        </div>

        {/* The caddy rail: whatever matters most right now. */}
        <div className="hs-rail">
          {advice.slice(0, 4).map((r) => (
            <div key={r.id} className={`hs-box ${r.tone === 'accent' ? 'mid' : ''} ${r.tone === 'warn' ? 'warn' : ''}`}>
              <span>{r.label}</span>
              <b>{r.value}</b>
              {r.detail && <i>{r.detail}</i>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
